import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import AsyncSelect from 'react-select/async';

import FormSection from '@/components/forms/FormSection';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Notification from '@/components/ui/Notification';
import Breadcrumb from '@/components/common/Breadcrumb';
import { arenaService, SportCategory } from '@/services/arenaService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import { mabarService, CreateEventPayload } from '@/services/mabarService';

// Player role interface
interface PlayerRole {
  id: string;
  role: string;
  jumlahPemain: number;
  biayaPerOrang: number;
}

// Form values interface
interface CreateMabarFormValues {
  namaEvent: string;
  deskripsiEvent: string;
  syaratKetentuan: string;
  tanggalEvent: string;
  jamMulai: string;
  jamSelesai: string;
  kota: string;
  lokasiArena: string;
  linkMapsVenue: string;
  linkGroup: string;
  tipeMabar: 'public' | 'private';
  kategoriOlahraga: string;
  biayaPemain: 'paid' | 'free';
}

// Initial values
const initialValues: CreateMabarFormValues = {
  namaEvent: '',
  deskripsiEvent: '',
  syaratKetentuan: '',
  tanggalEvent: '',
  jamMulai: '',
  jamSelesai: '',
  kota: '',
  lokasiArena: '',
  linkMapsVenue: '',
  linkGroup: '',
  tipeMabar: 'public',
  kategoriOlahraga: '',
  biayaPemain: 'free',
};

// URL validation regex
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

// Validation schema
const validationSchema = Yup.object({
  namaEvent: Yup.string().required('Nama event wajib diisi'),
  deskripsiEvent: Yup.string().required('Deskripsi event wajib diisi'),
  syaratKetentuan: Yup.string().required('Syarat dan ketentuan wajib diisi'),
  tanggalEvent: Yup.string().required('Tanggal event wajib diisi'),
  jamMulai: Yup.string().required('Jam mulai wajib diisi'),
  jamSelesai: Yup.string()
    .required('Jam selesai wajib diisi')
    .test('is-after-start', 'Jam selesai harus setelah jam mulai', function (value) {
      const { jamMulai } = this.parent;
      if (!jamMulai || !value) return true;
      return value > jamMulai;
    }),
  kota: Yup.string().required('Kota/Kabupaten wajib dipilih'),
  lokasiArena: Yup.string().required('Lokasi arena wajib diisi'),
  linkMapsVenue: Yup.string()
    .required('Link maps venue wajib diisi')
    .matches(urlRegex, 'Link maps harus berupa URL yang valid'),
  tipeMabar: Yup.string().oneOf(['public', 'private']).required('Tipe mabar wajib dipilih'),
  kategoriOlahraga: Yup.string().required('Kategori olahraga wajib dipilih'),
  biayaPemain: Yup.string().oneOf(['free', 'paid']).required('Biaya pemain wajib dipilih'),
});

// Breadcrumb items
const breadcrumbItems = [
  { label: 'Event', href: '/event' },
  { label: 'Mabar', href: '' },
];

const CreateMabarPage = () => {
  const navigate = useNavigate();
  const [sportCategories, setSportCategories] = useState<SportCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [playerRoles, setPlayerRoles] = useState<PlayerRole[]>([]);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<CreateMabarFormValues | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({ isOpen: false, message: '', variant: 'success' });

  // Role modal state
  const [roleFields, setRoleFields] = useState<
    { role: string; jumlahPemain: string; biayaPerOrang: string }[]
  >([{ role: '', jumlahPemain: '', biayaPerOrang: '' }]);

  // Track if event is free (for modal)
  const [isFreeEvent, setIsFreeEvent] = useState(true);

  // Kota autocomplete state
  const [selectedKota, setSelectedKota] = useState<KotaOption | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load sport categories
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const cats = await arenaService.getSportCategories();
        setSportCategories(cats);
      } catch (error) {
        console.error('Failed to load sport categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Calculate total players from roles
  const getTotalPlayersFromRoles = () => {
    return playerRoles.reduce((sum, role) => sum + role.jumlahPemain, 0);
  };

  // Calculate total players from modal roleFields
  const getTotalPlayersFromRoleFields = () => {
    return roleFields.reduce((sum, field) => {
      const num = parseInt(field.jumlahPemain) || 0;
      return sum + num;
    }, 0);
  };

  // Search kota API with debounce
  const searchKota = useCallback(
    (inputValue: string, callback: (options: KotaOption[]) => void) => {
      // Clear previous timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Don't search if less than 3 characters
      if (inputValue.length < 3) {
        callback([]);
        return;
      }

      // Debounce 500ms
      debounceRef.current = setTimeout(async () => {
        const options = await wilayahService.searchKota(inputValue);
        callback(options);
      }, 500);
    },
    []
  );

  // Custom styles for AsyncSelect to match form styling
  const kotaSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '0.375rem',
      borderColor: state.isFocused ? '#FC480D' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(252, 72, 13, 0.2)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#FC480D' : '#9ca3af',
      },
      minHeight: '38px',
      fontSize: '0.875rem',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#FC480D' : state.isFocused ? '#FEF2F2' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      fontSize: '0.875rem',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '0.875rem',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
    }),
    input: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
    }),
    noOptionsMessage: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
    }),
    loadingMessage: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
    }),
  };

  // Handle form submission
  const handleSubmit = (values: CreateMabarFormValues) => {
    // Validate that at least one role is defined
    if (playerRoles.length === 0) {
      alert('Silakan buat posisi pemain terlebih dahulu');
      return;
    }

    // Store values and show confirmation
    setPendingFormValues(values);
    setIsConfirmModalOpen(true);
  };

  // Handle confirmed save
  const handleConfirmedSave = async () => {
    if (!pendingFormValues) return;

    setIsSubmitting(true);
    setIsConfirmModalOpen(false);

    try {
      // Build API payload
      const payload: CreateEventPayload = {
        name: pendingFormValues.namaEvent,
        description: pendingFormValues.deskripsiEvent,
        tnc: pendingFormValues.syaratKetentuan,
        event_date: pendingFormValues.tanggalEvent,
        start_time: pendingFormValues.jamMulai,
        end_time: pendingFormValues.jamSelesai,
        lokasi_arena: pendingFormValues.lokasiArena,
        address: pendingFormValues.lokasiArena,
        city: pendingFormValues.kota,
        map_link: pendingFormValues.linkMapsVenue,
        link_group: pendingFormValues.linkGroup || '',
        field_id: null,
        sport_category_slug: pendingFormValues.kategoriOlahraga,
        type: pendingFormValues.biayaPemain,
        visibility: pendingFormValues.tipeMabar,
        config: {
          allow_guest: false,
          auto_confirm: false,
          max_guests_per_user: 0,
          player_roles: playerRoles.map((role) => ({
            role: role.role,
            slots: role.jumlahPemain,
            price: pendingFormValues.biayaPemain === 'free' ? 0 : role.biayaPerOrang,
          })),
        },
      };

      await mabarService.createEvent(payload);

      // Success - navigate back to mabar list
      navigate('/event');
    } catch (error: any) {
      console.error('Failed to save event:', error);
      setNotification({
        isOpen: true,
        message: error?.message || 'Gagal menyimpan event. Silakan coba lagi.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    window.history.go(-1);
  };

  // Add new role field in modal
  const handleAddRoleField = () => {
    setRoleFields([
      ...roleFields,
      { role: '', jumlahPemain: '', biayaPerOrang: isFreeEvent ? '0' : '' },
    ]);
  };

  // Remove role field in modal
  const handleRemoveRoleField = (index: number) => {
    if (roleFields.length > 1) {
      setRoleFields(roleFields.filter((_, i) => i !== index));
    }
  };

  // Update role field in modal
  const handleRoleFieldChange = (index: number, field: string, value: string) => {
    const updated = [...roleFields];
    updated[index] = { ...updated[index], [field]: value };
    setRoleFields(updated);
  };

  // Save roles from modal
  const handleSaveRoles = () => {
    // Validate all fields are filled
    const validRoles = roleFields.filter(
      (f) => f.role.trim() !== '' && f.jumlahPemain !== '' && parseInt(f.jumlahPemain) > 0
    );

    if (validRoles.length === 0) {
      alert('Silakan isi minimal satu posisi pemain dengan lengkap');
      return;
    }

    // Convert to PlayerRole format
    const newRoles: PlayerRole[] = validRoles.map((f, i) => ({
      id: `role-${Date.now()}-${i}`,
      role: f.role.trim(),
      jumlahPemain: parseInt(f.jumlahPemain),
      biayaPerOrang: parseFloat(f.biayaPerOrang) || 0,
    }));

    setPlayerRoles(newRoles);
    setIsRoleModalOpen(false);
  };

  // Open role modal for editing
  const handleOpenRoleModal = (isFree: boolean) => {
    setIsFreeEvent(isFree);
    // Pre-fill with existing roles if any
    if (playerRoles.length > 0) {
      setRoleFields(
        playerRoles.map((r) => ({
          role: r.role,
          jumlahPemain: r.jumlahPemain.toString(),
          biayaPerOrang: isFree ? '0' : r.biayaPerOrang.toString(),
        }))
      );
    } else {
      setRoleFields([{ role: '', jumlahPemain: '', biayaPerOrang: isFree ? '0' : '' }]);
    }
    setIsRoleModalOpen(true);
  };

  // Cancel role modal
  const handleCancelRoleModal = () => {
    setIsRoleModalOpen(false);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    if (value === 0) return 'Free';
    return `Rp. ${value.toLocaleString('id-ID')}`;
  };

  // Sport category options for dropdown
  const sportCategoryOptions = sportCategories.map((cat) => ({
    value: cat.slug || cat.name,
    label: cat.name,
  }));

  return (
    <div>
      <Breadcrumb items={breadcrumbItems} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Informasi Dasar */}
              <FormSection title="Informasi Dasar">
                <Input
                  name="namaEvent"
                  label="Nama Event"
                  placeholder="Isikan nama arena"
                  required
                />

                <Textarea
                  name="deskripsiEvent"
                  label="Deskripsi Event"
                  placeholder="Isikan deskripsi Event"
                  rows={4}
                  required
                />

                <Textarea
                  name="syaratKetentuan"
                  label="Syarat dan ketentuan Event"
                  placeholder="Isikan syarat dan ketentuan Event"
                  rows={4}
                  required
                />

                <Input name="tanggalEvent" label="Tanggal Event" type="date" required />

                <div className="grid grid-cols-2 gap-4">
                  <Input name="jamMulai" label="Jam Mulai" type="time" required />
                  <Input name="jamSelesai" label="Jam Selesai" type="time" required />
                </div>

                <Input
                  name="lokasiArena"
                  label="Lokasi Arena"
                  placeholder="Lokasi Arena"
                  required
                />

                {/* Kota/Kabupaten Autocomplete */}
                <div className="w-full">
                  <label className="block text-sm text-gray-600 mb-2">
                    Kota/Kabupaten
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <AsyncSelect<KotaOption>
                    cacheOptions
                    loadOptions={searchKota}
                    value={selectedKota}
                    onChange={(option) => {
                      setSelectedKota(option);
                      setFieldValue('kota', option?.nama || '');
                    }}
                    placeholder="Ketik minimal 3 huruf untuk mencari..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length < 3 ? 'Ketik minimal 3 huruf' : 'Tidak ada hasil ditemukan'
                    }
                    loadingMessage={() => 'Mencari...'}
                    isClearable
                    styles={kotaSelectStyles}
                    getOptionLabel={(option) => option.namaLengkap}
                    getOptionValue={(option) => option.value}
                  />
                  <ErrorMessage name="kota" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div className="w-full">
                  <label className="block text-sm text-gray-600 mb-2">
                    Link Maps Venue
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Icon icon="mdi:map-marker-outline" className="w-5 h-5" />
                    </span>
                    <Field
                      name="linkMapsVenue"
                      type="text"
                      placeholder="Link Location"
                      className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm outline-none focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <ErrorMessage
                    name="linkMapsVenue"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </FormSection>

              {/* Right Column - Tim & Pemain */}
              <FormSection title="Tim & Pemain">
                {/* Link Group (Optional) */}
                <Input
                  name="linkGroup"
                  label="Link Group"
                  placeholder="Link WhatsApp/Telegram Group (Opsional)"
                />

                {/* Tipe Mabar */}
                <div className="w-full">
                  <label className="block text-sm text-gray-600 mb-2">
                    Tipe Mabar
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFieldValue('tipeMabar', 'public')}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        values.tipeMabar === 'public'
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-500'
                      }`}
                    >
                      Umum
                      {values.tipeMabar === 'public' && (
                        <Icon icon="mdi:check-circle" className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFieldValue('tipeMabar', 'private')}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        values.tipeMabar === 'private'
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-500'
                      }`}
                    >
                      Private
                      {values.tipeMabar === 'private' && (
                        <Icon icon="mdi:check-circle" className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="tipeMabar"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Kategori Olahraga */}
                <Select
                  name="kategoriOlahraga"
                  label="Kategori Olahraga"
                  placeholder={isLoadingCategories ? 'Loading...' : 'Pilih kategori olahraga'}
                  options={sportCategoryOptions}
                  required
                />

                {/* Biaya Pemain */}
                <div className="w-full">
                  <label className="block text-sm text-gray-600 mb-2">
                    Biaya Pemain
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFieldValue('biayaPemain', 'free')}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        values.biayaPemain === 'free'
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-500'
                      }`}
                    >
                      Gratis
                      {values.biayaPemain === 'free' && (
                        <Icon icon="mdi:check-circle" className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFieldValue('biayaPemain', 'paid')}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        values.biayaPemain === 'paid'
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-500'
                      }`}
                    >
                      Berbayar
                      {values.biayaPemain === 'paid' && (
                        <Icon icon="mdi:check-circle" className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="biayaPemain"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Posisi Pemain */}
                <div className="w-full">
                  <label className="block text-sm text-gray-600 mb-2">
                    Posisi Pemain
                    <span className="text-red-500 ml-1">*</span>
                  </label>

                  {/* Player Roles Table */}
                  {playerRoles.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">
                              Posisi
                            </th>
                            <th className="px-4 py-2 text-center font-medium text-gray-600">
                              Jumlah
                            </th>
                            <th className="px-4 py-2 text-right font-medium text-gray-600">
                              Biaya/Orang
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {playerRoles.map((role) => (
                            <tr key={role.id} className="border-t border-gray-100">
                              <td className="px-4 py-2 text-gray-800">{role.role}</td>
                              <td className="px-4 py-2 text-center text-gray-800">
                                {role.jumlahPemain}
                              </td>
                              <td className="px-4 py-2 text-right text-gray-800">
                                {values.biayaPemain === 'free'
                                  ? 'Free'
                                  : formatCurrency(role.biayaPerOrang)}
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t border-gray-200 bg-gray-50 font-medium">
                            <td className="px-4 py-2 text-gray-800">Total</td>
                            <td className="px-4 py-2 text-center text-gray-800">
                              {getTotalPlayersFromRoles()}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-800">
                              {values.biayaPemain === 'free'
                                ? 'Free'
                                : formatCurrency(
                                    playerRoles.reduce(
                                      (sum, r) => sum + r.jumlahPemain * r.biayaPerOrang,
                                      0
                                    )
                                  )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Buat Posisi Pemain Button */}
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => handleOpenRoleModal(values.biayaPemain === 'free')}
                    className="border-primary-500 text-primary-500 hover:bg-primary-50"
                  >
                    <Icon icon="mdi:plus" className="w-5 h-5 mr-2" />
                    {playerRoles.length > 0 ? 'Tambah Kuota Mabar' : 'Buat Posisi Pemain'}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    borderClass="border-gray-300"
                    textClass="text-gray-700"
                    hoverBgClass="hover:bg-gray-50"
                    hoverBorderClass="hover:border-gray-400"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={playerRoles.length === 0}>
                    Save
                  </Button>
                </div>
              </FormSection>
            </div>
          </Form>
        )}
      </Formik>

      {/* Player Role Modal */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={handleCancelRoleModal}
        title="Buat Role Pemain"
        size="md"
      >
        <div className="space-y-4">
          {/* Slot Info */}
          <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-gray-600">Total Slot Pemain:</span>
            <span className="font-medium text-gray-800">{getTotalPlayersFromRoleFields()}</span>
          </div>

          {/* Header */}
          <div className="grid grid-cols-3 gap-4 text-sm font-medium text-gray-600 px-1">
            <div>Role</div>
            <div>Jumlah Pemain</div>
            <div>Biaya per orang</div>
          </div>

          {/* Role Fields */}
          {roleFields.map((field, index) => (
            <div key={index} className="grid grid-cols-3 gap-4 items-start">
              <input
                type="text"
                value={field.role}
                onChange={(e) => handleRoleFieldChange(index, 'role', e.target.value)}
                placeholder={`Role ${index + 1}`}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:outline-none focus:border-primary-500"
              />
              <input
                type="number"
                value={field.jumlahPemain}
                onChange={(e) => handleRoleFieldChange(index, 'jumlahPemain', e.target.value)}
                placeholder="Jumlah Pemain"
                min={1}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:outline-none focus:border-primary-500"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={isFreeEvent ? '0' : field.biayaPerOrang}
                  onChange={(e) => handleRoleFieldChange(index, 'biayaPerOrang', e.target.value)}
                  placeholder={isFreeEvent ? 'Gratis' : 'Rp. Biaya per orang'}
                  min={0}
                  disabled={isFreeEvent}
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:outline-none focus:border-primary-500 ${isFreeEvent ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
                {roleFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveRoleField(index)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon icon="mdi:close" className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add Button */}
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={handleAddRoleField}
            className="border-primary-500 text-primary-500 hover:bg-primary-50"
          >
            <Icon icon="mdi:plus" className="w-5 h-5 mr-2" />
            Tambah
          </Button>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelRoleModal}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSaveRoles}>
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Konfirmasi Simpan"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">Apakah Anda yakin ingin menyimpan event mabar ini?</p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmedSave}
              loading={isSubmitting}
            >
              Ya, Simpan
            </Button>
          </div>
        </div>
      </Modal>

      {/* Error Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.variant === 'error' ? 'Error' : 'Sukses'}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default CreateMabarPage;
